import _ from 'lodash';

import Entity from './Entity';
import PaymentApplicationMonth from './PaymentApplicationMonth';

import featureFlags from '../featureFlags';
import AccessDevice from './AccessDevice';
import AlertConfiguration from './AlertConfiguration';
import BillingProfile from './BillingProfile';
import Fee from './Fee';
import Rate from './Rate';
import SurveyQuestion from './SurveyQuestion';

const facilitySettingsEntityFieldsWithStaticAllowedValues = {
    use_ach: {
        options: [
            { value: true, label: 'On' },
            { value: false, label: 'Off' }
        ]
    },
    use_credit_card: {
        options: [
            { value: true, label: 'On' },
            { value: false, label: 'Off' }
        ]
    },
    display_organization_invoice_notes: {
        options: [
            { value: true, label: 'Yes' },
            { value: false, label: 'No' }
        ]
    },
    month_close_day: {
        options: [
            { value: 'ONE', label: '1st' },
            { value: 'TWO', label: '2nd' },
            { value: 'THREE', label: '3rd' },
            { value: 'FOUR', label: '4th' },
            { value: 'FIVE', label: '5th' },
            { value: 'SIX', label: '6th' },
            { value: 'SEVEN', label: '7th' },
            { value: 'EIGHT', label: '8th' },
            { value: 'NINE', label: '9th' },
            { value: 'TEN', label: '10th' },
            { value: 'ELEVEN', label: '11th' },
            { value: 'TWELVE', label: '12th' },
            { value: 'THIRTEEN', label: '13th' },
            { value: 'FOURTEEN', label: '14th' },
            { value: 'FIFTEEN', label: '15th' },
            { value: 'SIXTEEN', label: '16th' },
            { value: 'SEVENTEEN', label: '17th' },
            { value: 'EIGHTEEN', label: '18th' },
            { value: 'NINETEEN', label: '19th' },
            { value: 'TWENTY', label: '20th' },
            { value: 'TWENTY_ONE', label: '21st' },
            { value: 'TWENTY_TWO', label: '22nd' },
            { value: 'TWENTY_THREE', label: '23rd' },
            { value: 'TWENTY_FOUR', label: '24th' },
            { value: 'TWENTY_FIVE', label: '25th' },
            { value: 'TWENTY_SIX', label: '26th' },
            { value: 'TWENTY_SEVEN', label: '27th' },
            { value: 'TWENTY_EIGHT', label: '28th' },
            { value: 'LAST', label: 'Last' }
        ]
    }
};

const facilitySettingsEntityFieldsWithDynamicAllowedValues = {
    prior_month_application: { enumName: 'PriorMonthApplicationEnum' },
    future_month_application: { enumName: 'FutureMonthApplicationEnum' },
    month_close_day: { enumName: 'MonthCloseDayEnum' },
    month_close_day_type: { enumName: 'MonthCloseDayTypeEnum' },
    admin_process_payments: { enumName: 'AdminProcessPaymentsEnum' },
    accounting_method: { enumName: 'AccountingMethodEnum' },
};

export const monthCloseDayLabels = {
    options: [
        { value: 'ONE', label: '1st' },
        { value: 'TWO', label: '2nd' },
        { value: 'THREE', label: '3rd' },
        { value: 'FOUR', label: '4th' },
        { value: 'FIVE', label: '5th' },
        { value: 'SIX', label: '6th' },
        { value: 'SEVEN', label: '7th' },
        { value: 'EIGHT', label: '8th' },
        { value: 'NINE', label: '9th' },
        { value: 'TEN', label: '10th' },
        { value: 'ELEVEN', label: '11th' },
        { value: 'TWELVE', label: '12th' },
        { value: 'THIRTEEN', label: '13th' },
        { value: 'FOURTEEN', label: '14th' },
        { value: 'FIFTEEN', label: '15th' },
        { value: 'SIXTEEN', label: '16th' },
        { value: 'SEVENTEEN', label: '17th' },
        { value: 'EIGHTEEN', label: '18th' },
        { value: 'NINETEEN', label: '19th' },
        { value: 'TWENTY', label: '20th' },
        { value: 'TWENTY_ONE', label: '21st' },
        { value: 'TWENTY_TWO', label: '22nd' },
        { value: 'TWENTY_THREE', label: '23rd' },
        { value: 'TWENTY_FOUR', label: '24th' },
        { value: 'TWENTY_FIVE', label: '25th' },
        { value: 'TWENTY_SIX', label: '26th' },
        { value: 'TWENTY_SEVEN', label: '27th' },
        { value: 'TWENTY_EIGHT', label: '28th' },
        { value: 'LAST', label: 'Last' }
    ]
};

export class Facility {
    fields = {
        id: {
            value: null
        },
        uid: {
            value: null
        },
        timezone: {
            value: null
        },
        facility_name: {
            value: null
        },
        address: {
            value: null
        },
        on_purchase_emails: {
            value: null
        },
        email_on_purchase_monthly: {
            value: null
        },
        monthly_invoice_logo: {
            value: null
        },
        monthly_invoice_address: {
            value: null
        },
        terms_and_conditions: {
            value: null
        },
        terms_and_conditions_summary: {
            value: null
        },
        accessSystem: {
            value: null
        }
    };


    static createFromQueryValues(values) {
        const facility = new this();

        for (let field in facility.fields) {
            if (values.hasOwnProperty(field)) {
                switch (field) {
                    default:
                        facility.fields[field].value = values[field];
                }
            }
        }

        return facility;
    }
}

export class FacilitySettings {
    constructor(facility_id) {
        this.fields.facility_id.value = facility_id;
    }

    fields = {
        facility_id: {
            value: null
        },
        display: {
            value: null
        },
        require_car_info: {
            value: null
        },
        decline_recurring: {
            value: null
        },
        parking_period: {
            value: null
        },
        billing_timing: {
            value: null
        },
        billing_day: {
            value: null
        },
        payment_due_day: {
            value: null
        },
        first_month_proration: {
            value: null
        },
        first_month_proration_day: {
            value: null
        },
        display_organization_invoice_notes: {
            value: null
        },
        organization_invoice_notes: {
            value: null
        },
        invoice_notes: {
            value: null
        },
        access_device_message: {
            value: null
        },
        lost_device_request: {
            value: null
        },
        mail_invoice_option: {
            value: null
        },
        termination_notice_day: {
            value: null
        },
        provide_survey: {
            value: null
        },
        terms_and_conditions: {
            value: null
        },
        terms_and_conditions_summary: {
            value: null
        },
        next_steps: {
            value: null
        },
        use_ach: {
            value: null
        },
        use_credit_card: {
            value: null,
        },
        fees: {
            activation: {
                value: []
            },
            lost_device: {
                value: []
            },
            credit_card: {
                value: []
            },
            mail_invoice: {
                value: []
            },
            late: {
                value: []
            }
        },
        access_devices: {
            value: []
        },
        survey_questions: {
            purchase: {
                value: []
            },
            termination: {
                value: []
            }
        },
        alert_configurations: {
            value: []
        },
        validation_products: {
            value: []
        },
        prior_month_application: {
            value: null
        },
        future_month_application: {
            value: null
        },
        month_close_day: {
            value: null
        },
        month_close_day_type: {
            value: null
        },
        payment_application_months: {
            value: []
        },
        admin_process_payments: {
            value: null
        },
        manage_recurring_payments: {
            value: null
        },
        edit_payment_options: {
            value: null
        },
        payment_types: {
            value: []
        },
        accounting_method: {
            value: null
        },
        organization_id: {
            value: null
        },
    }

    static createWithDefaults(facilityId) {
        const settings = new this(facilityId);
        settings.fields.display.value = 'YES';
        settings.fields.require_car_info.value = 'OPTIONAL';
        settings.fields.decline_recurring.value = 'ALLOWED';
        settings.fields.parking_period.value = 'MONTHLY';
        settings.fields.billing_timing.value = 'CALENDAR_DAY';
        settings.fields.billing_day.value = 15;
        settings.fields.month_close_day_type.value = 'CALENDAR';
        settings.fields.payment_due_day.value = 1;
        settings.fields.display_organization_invoice_notes.value = false;
        settings.fields.lost_device_request.value = false;
        settings.fields.mail_invoice_option.value = false;
        settings.fields.termination_notice_day.value = 2;
        settings.fields.provide_survey.value = false;
        settings.fields.first_month_proration.value = 'DAILY';
        settings.fields.use_ach.value = false;
        settings.fields.use_credit_card.value = true;
        settings.fields.prior_month_application.value = 'MANUAL';
        settings.fields.future_month_application.value = 'NOT_ALLOWED';
        settings.fields.payment_application_months.value = PaymentApplicationMonth.createDefaultYearOfMonths(facilityId);
        settings.fields.admin_process_payments.value = 'DO_NOT_DISPLAY';
        settings.fields.manage_recurring_payments.value = 'DO_NOT_DISPLAY';
        settings.fields.edit_payment_options.value = 'DO_NOT_DISPLAY';
        settings.fields.payment_types.value = [];
        settings.fields.accounting_method.value = null;

        return settings;
    }

    static createFromQueryValues(values) {
        const facilitySettings = new this();

        for (let field in facilitySettings.fields) {
            if (values.hasOwnProperty(field)) {
                switch (field) {
                    case 'fees':
                        if (Array.isArray(values[field])) {
                            for (let type in facilitySettings.fields[field]) {
                                const feesOfType = values[field].filter(e => e.type === type.toUpperCase());
                                if (feesOfType.length) {
                                    facilitySettings.fields[field][type].value = [];
                                    for (let fee of feesOfType) {
                                        facilitySettings.fields[field][type].value.push(Fee.createFromQueryValues(fee));
                                    }
                                }
                            }
                        }
                        break;
                    case 'access_devices':
                        if (Array.isArray(values[field])) {
                            for (let accessDevice of values[field]) {
                                facilitySettings.fields[field].value.push(AccessDevice.createFromQueryValues(accessDevice));
                            }
                        }
                        break;
                    case 'survey_questions':
                        if (Array.isArray(values[field])) {
                            for (let type in facilitySettings.fields[field]) {
                                const surveyQuestionsOfType = values[field].filter(e => e.type === type.toUpperCase());
                                if (surveyQuestionsOfType.length) {
                                    facilitySettings.fields[field][type].value = [];
                                    for (let surveyQuestion of surveyQuestionsOfType) {
                                        facilitySettings.fields[field][type].value.push(SurveyQuestion.createFromQueryValues(surveyQuestion));
                                    }
                                }
                            }
                        }
                        break;
                    case 'alert_configurations':
                        if (Array.isArray(values[field])) {
                            for (let alertConfiguration of values[field]) {
                                facilitySettings.fields[field].value.push(AlertConfiguration.createFromQueryValues(alertConfiguration));
                            }
                        }
                        break;
                    case 'validation_products':
                        facilitySettings.fields[field].value = values[field] || [];
                        break;
                    case 'payment_application_months':
                        if (Array.isArray(values[field])) {
                            for (let paymentApplicationMonth of values[field]) {
                                facilitySettings.fields[field].value.push(PaymentApplicationMonth.createFromQueryValues(paymentApplicationMonth));
                            }
                        }
                        break;
                    case 'provide_survey':
                        facilitySettings.fields[field].value = !!values[field];
                        break;
                    default:
                        facilitySettings.fields[field].value = values[field];
                }
            }
        }

        return facilitySettings;
    }


    static getFieldAllowedValues(fields) {
        const promises = [];

        promises.push(new Promise((resolve, reject) => {
            Entity.getFieldAllowedValues(
                facilitySettingsEntityFieldsWithStaticAllowedValues,
                facilitySettingsEntityFieldsWithDynamicAllowedValues,
                fields
            ).then(data => {
                resolve(data);
            }, error => {
                reject(error);
            });
        }));

        promises.push(new Promise((resolve, reject) => {
            Rate.getFieldAllowedValues([
                'display',
                'require_car_info',
                'first_month_proration',
                'first_month_proration_day',
                'lost_device_request',
            ]).then(data => {
                resolve(data);
            }, error => {
                reject(error);
            });
        }));

        promises.push(new Promise((resolve, reject) => {
            BillingProfile.getFieldAllowedValues([
                'parking_period',
                'billing_timing',
                'decline_recurring',
                'billing_day',
                'payment_due_day',
                'mail_invoice_option',
                'termination_notice_day',
                'provide_survey'
            ]).then(data => {
                resolve(data);
            }, error => {
                reject(error);
            });
        }));

        return new Promise((resolve, reject) => {
            Promise.all(promises).then(responses => {
                let values = {};
                for (let response of responses) {
                    values = { ...values, ...response };
                }
                resolve(values);
            }, error => {
                reject(error);
            });
        });
    }


    getQueryFieldValues() {
        const values = _.cloneDeep(FacilitySettingsInput);
    
        for (let field in values) {
            switch (field) {
                case 'fees':
                case 'survey_questions':
                    for (let elementType in this.fields[field]) {
                        if (this.fields[field][elementType].value.length) {
                            for (let element of this.fields[field][elementType].value) {
                                const elementQueryFieldValues = element.getQueryFieldValues();
                                if (elementQueryFieldValues) {
                                    values[field] = values[field] || [];
                                    values[field].push(elementQueryFieldValues);
                                }
                            }
                        }
                    }
                    break;
                case 'payment_application_months':
                case 'access_devices':
                case 'alert_configurations':
                    if (this.fields[field].value.length) {
                        values[field] = [];
                        for (let element of this.fields[field].value) {
                            values[field].push(element.getQueryFieldValues());
                        }
                    }
                    else {
                        values[field] = null;
                    }
                    break;
                default:
                    values[field] = (this.fields[field].value === '') ?
                        null :
                        this.fields[field].value;
            }
        }

        return values;
    }
}

export const FacilitySettingsInput = {
    facility_id: null,
    display: null,
    require_car_info: null,
    decline_recurring: null,
    parking_period: null,
    billing_timing: null,
    billing_day: null,
    payment_due_day: null,
    first_month_proration: null,
    first_month_proration_day: null,
    display_organization_invoice_notes: null,
    organization_invoice_notes: null,
    invoice_notes: null,
    access_device_message: null,
    lost_device_request: null,
    mail_invoice_option: null,
    termination_notice_day: null,
    provide_survey: null,
    terms_and_conditions: null,
    terms_and_conditions_summary: null,
    next_steps: null,
    fees: null,
    access_devices: null,
    survey_questions: null,
    alert_configurations: null,
    validation_products: null,
    use_ach: null,
    use_credit_card: null,
    prior_month_application: null,
    future_month_application: null,
    month_close_day: 'ONE',
    month_close_day_type: null,
    payment_application_months: null,
    admin_process_payments: null,
    manage_recurring_payments: null,
    edit_payment_options: null,
    payment_types: null,
    accounting_method: null,
};
